import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { BrandLogo } from './BrandLogo';
import { Button } from './ui/button';
import { api } from '../lib/trpc/client';
import { clearSessionTokens, clearStoredAccounts } from '../lib/session';
import { t } from '../lib/i18n';

export function SidebarNav() {
  const navigate = useNavigate();
  const { circleId } = useParams<{ circleId: string }>();
  const [activeCircleId, setActiveCircleId] = useState<string | null>(null);
  const [activeCircleName, setActiveCircleName] = useState<string>('');
  const [accountName, setAccountName] = useState<string>('');

  const resolvedCircleId = circleId ?? activeCircleId;

  const items = [
    { label: t('タイムライン', 'Timeline'), to: resolvedCircleId ? `/${resolvedCircleId}/timeline` : '/circles' },
    { label: 'DM', to: resolvedCircleId ? `/${resolvedCircleId}/dm` : '/circles' },
    { label: t('グループ', 'Groups'), to: resolvedCircleId ? `/${resolvedCircleId}/groups` : '/circles' },
    { label: t('招待', 'Invites'), to: '/invites' },
    { label: t('設定', 'Settings'), to: '/settings' }
  ];

  useEffect(() => {
    void Promise.all([
      api.circle.getActiveCircle(),
      api.circle.getActiveCircleId(),
      api.auth.me()
    ])
      .then(([active, activeId, me]) => {
        setActiveCircleId(activeId.circleId);
        setActiveCircleName(active?.name ?? '');
        setAccountName(me.scratchUsername);
      })
      .catch(() => {
        setActiveCircleId(null);
        setActiveCircleName('');
        setAccountName('');
      });
  }, [circleId]);

  const handleLogoutCurrent = async () => {
    try {
      await api.auth.logout();
    } catch {
      // Ignore server-side logout failures on local switch operations.
    }

    clearSessionTokens();
    clearStoredAccounts();
    navigate('/', { replace: true });
  };

  return (
    <aside className="border-r border-border p-4">
      <div className="mb-6">
        <BrandLogo className="inline-flex items-center gap-2.5" textClassName="font-mono text-[1.45rem] font-semibold leading-none" />
      </div>

      <nav className="space-y-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `block rounded-md px-3 py-2 text-sm ${isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'}`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-6 space-y-2 border-t border-border pt-4">
        <p className="text-xs font-medium tracking-normal text-muted-foreground">{t('アクティブサークル', 'Active Circle')}</p>
        <div className="rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
          {activeCircleName || t('未選択', 'Not selected')}
        </div>
        <Button variant="ghost" size="sm" className="w-full" onClick={() => navigate('/circles')}>
          {t('サークル切り替え', 'Switch Circle')}
        </Button>
        <p className="pt-1 text-xs font-medium tracking-normal text-muted-foreground">{t('アカウント', 'Account')}</p>
        <div className="rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
          {accountName || t('未取得', 'Unknown')}
        </div>
        <Button variant="outline" size="sm" className="w-full" onClick={() => void handleLogoutCurrent()}>
          {t('ログアウト', 'Logout')}
        </Button>
      </div>
    </aside>
  );
}
